import { useMsal } from "@azure/msal-react";
import {
    CommandBar, IButtonProps,
    ICommandBarItemProps,
    IContextualMenuItem,
    MessageBar, MessageBarType,
    Text,
    useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import saveAs from "file-saver";
import BreadcrumbNavigation from "global/Application/BreadcrumbNavigation";
import { useApplication } from "global/Application/Context";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupConfigurationShowInActives, notificationRemove } from "global/Reducer/dataSlice";
import { IComponentContextMenu } from "model/Application";
import { IGLWindow } from "model/GroupLibraryWindow";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useHistory } from "react-router";
import { EViewType, emitCustomEvent, fadeIn } from "../../../global/Application";
import { ESaveType, loadSnapshotAction } from '../../../global/Application/dataActions';
import {
    ELoadStatus,
    setIsNavPinned, setProcessing, setViewType, updateDebug
} from "../../../global/Reducer/statusSlice";


const overflowProps: IButtonProps = { ariaLabel: 'More commands' };


interface IEditorNavbarProps extends TComponentConnectedProps, IComponentContextMenu {
    hideShowDeactivated?: boolean;
    unStickyNavbar?: boolean;
    hideSave?: boolean;
}

const EditorNavbar = (props: IEditorNavbarProps) => {
    const history = useHistory();

    const authentication = useAuthentication();
    const appContext = useApplication();

    const dispatch = useDispatch();
    const theme = useTheme();

    const refStickyBar = useRef(null);
    const [sticky, setSticky] = useState(false);

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    let menuOverflow: ICommandBarItemProps[] = [];
    const menuFar: ICommandBarItemProps[] = [];

    let menu: ICommandBarItemProps[] = [];
    const subMenuSave: IContextualMenuItem[] = [];

    const isPowerUser = authentication.authorizations?.roles['BCBS - Power User'];
    const isReadOnlyPowerUser = authentication.authorizations?.roles['BCBS - Read Only Power User - BDS'];

    function onScroll(_: any) {
        if (!sticky && window.pageYOffset >= 50 && props.state.status.isNavPinned)
            // if (!isCheckList)
            if (!props.unStickyNavbar)
                setSticky(true);

        if (sticky && window.pageYOffset < 50)
            setSticky(false);
    };

    useEffect(() => {
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [sticky, props.state.status.isNavPinned]);



    const styleStickyBar = useMemo(() => {
        if (!sticky)
            return {
                transition: '0.1s',
                backgroundColor: theme.semanticColors.bodyBackground,
            } as React.CSSProperties;

        const style: React.CSSProperties = {
            position: 'fixed',
            top: '0',
            width: '100%',
            zIndex: 100000,
            borderBottom: '1px solid gray',
            transition: '0.1s',
            backgroundColor: theme.semanticColors.bodyBackground,
        };

        return style;
    }, [sticky, theme]);

    menuFar.push({
        key: 'share',
        text: 'Share',
        cacheKey: 'myCacheKey', // changing this key will invalidate this item's cache
        iconProps: { iconName: 'Share' },
        subMenuProps: {
            items: [
                {
                    key: 'emailMessage',
                    text: 'Email Link',
                    iconProps: { iconName: 'Mail' },
                    onClick: () => {
                        const subject = `Group Library - ${props.state.data.snapshot.bcbs_name}`;
                        const body = `Regarding ${window.location.href}\r\n`;
                        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                    }
                },
                {
                    key: 'calendarEvent',
                    text: 'Copy Page Link',
                    iconProps: { iconName: 'PageLink' },
                    onClick: () => {
                        navigator.clipboard.writeText(window.location.href);
                    }
                },
            ],
        },
    },
        {
            key: 'pin',
            text: '',
            iconProps: { iconName: props.state.status.isNavPinned ? 'PinnedSolid' : 'Pin' },
            onClick: (event, item) => {
                dispatch(setIsNavPinned());
            }
        }

    );

    menu.push({
        key: 'back',
        text: 'Back',
        iconProps: { iconName: 'Back' },
        onClick: () => {
            history.goBack();
        }
    });

    if (true) {
        subMenuSave.push({
            key: 'createSnapshot',
            text: 'Create Merged Snapshot',
            iconProps: { iconName: 'BrowserTabScreenshot' },
            onClick: () => {
                appContext.SaveAsync({
                    saveType: ESaveType.EManual,
                    isMergeSave: true,
                });
            },
        });

        if (!props.hideSave)
            menu.push({
                key: 'save',
                text: 'Save',
                iconProps: { iconName: 'Save' },
                disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
                subMenuProps: {
                    items: subMenuSave
                }
            });
    }

    if (props?.contextMenu?.items) {
        const mergeItems = props.contextMenu.items.filter(t => t.key.startsWith('merge') || t.key.startsWith('compare'));
        menu.push(...mergeItems);
    }

    menu.push({
        key: 'showDeactivated',
        text: 'Show Deactivated',
        iconProps: {
            iconName: props.state?.data?.json?.showInActives?.value ? 'RedEye' : 'Hide',
            styles: {
            }
        },
        disabled: props.state.status.dataSavingStatus === ELoadStatus.saving,
        buttonStyles: {
            root: {
                backgroundColor: !props?.state?.data?.json?.showInActives?.value ? undefined : theme.semanticColors.warningBackground,
            }
        },
        onClick: () => {
            dispatch(addUpdateGroupConfigurationShowInActives(
                {
                    type: 'bool',
                    value: !props?.state?.data?.json?.showInActives?.value
                }
            ));

        }
    });



    const adminMenu = () => {

        const debugSubMenu: IContextualMenuItem[] = [];

        if (isPowerUser)
            debugSubMenu.push({
                key: 'debugViewMode',
                text: props.state.status.viewType === EViewType.ViewOnly
                    ? '✏️ Switch to Edit'
                    : '👁️ Switch to View',
                onClick: (event, item) => {
                    dispatch(updateDebug({ autoSaveOff: true }));

                    if (props.state.status.viewType === EViewType.ViewOnly)
                        dispatch(setViewType(EViewType.Modify));

                    else
                        dispatch(setViewType(EViewType.ViewOnly));

                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'liveload',
                text: props.state.status.debug.liveload
                    ? '🟢 Reload with live configuration'
                    : '⚫ Reload with live configuration',
                onClick: (event, item) => {
                    dispatch(updateDebug({ liveload: true }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                    dispatch(loadSnapshotAction(
                        dataSource,
                        props.state.data.account?.accountid!,
                        {
                            primarySnapshotId: props.state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                        }
                    ));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'diagnosis',
                text: props.state.status.debug.diagnosis
                    ? '🟢 Diagnosis'
                    : '⚫ Diagnosis',
                onClick: (event, item) => {
                    dispatch(updateDebug({ diagnosis: !props.state.status.debug.diagnosis }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                }
            });


        if (isPowerUser)
            debugSubMenu.push({
                key: 'debugAdminOverride',
                text: props.state.status.debug.admin
                    ? '🟢 Admin override'
                    : '⚫ Admin override',
                onClick: (event, item) => {
                    dispatch(updateDebug({ admin: !props.state.status.debug.admin }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'debugAutoSaveOff',
                text: props.state.status.debug.autoSaveOff
                    ? '🟢 Autosave is off'
                    : '⚫ Autosave is on',
                onClick: (event, item) => {
                    dispatch(updateDebug({ autoSaveOff: !props.state.status.debug.autoSaveOff }));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'debugDownloadDataModel',
                text: '🌐 Download Data Model',
                onClick: (event, item) => {
                    (async () => {
                        try {

                            dispatch(setProcessing(true));
                            const response = await dataSource.getGenerateDataDictionaryExtract();
                            saveAs(response.blob, response.fileName);

                        } catch (error) {
                            // lets create a new one is payload is not providing one
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });

                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

        return ({
            key: 'Admin',
            text: 'Admin',
            iconProps: { iconName: 'Settings' },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
            subMenuProps: {
                items: debugSubMenu
            }
        } as ICommandBarItemProps);
    }

    const onRenderNotificationBar = () => {
        const elements: JSX.Element[] = [];

        if (props.state.data.json.showInActives?.value)
            elements.push(
                <MessageBar key={"notification.showInActives"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing only deactivated records. All records below are currently deactivated.
                </MessageBar>
            )

        if (props.state.status.debug.liveload)
            elements.push(
                <MessageBar key={"notification.liveload"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    You are currently live loading the configuration.
                </MessageBar>
            )

        if (props.state.status.debug.admin)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Admin mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.developer)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Developer mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.noVisibility)
            elements.push(
                <MessageBar key={"notification.noVisibility"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Visibility loading has been disabled.
                </MessageBar>
            )

        if (props.state.data.json.MNIL?.value)
            elements.push(
                <MessageBar key={"notification.MNIL"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing fields for MNIL.
                </MessageBar>
            )

        if (((window as IGLWindow).preview))
            elements.push(
                <MessageBar key={"notification.preview"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Preview Mode has been enabled
                </MessageBar>
            )

        props.state.data.notifications?.forEach((notification, index) => {
            elements.push(
                <MessageBar
                    key={notification.id}
                    className={fadeIn}
                    messageBarType={MessageBarType.warning}
                    onDismiss={(ev) => {
                        dispatch(notificationRemove(notification.id));
                    }}
                    dismissButtonAriaLabel="Close"
                >
                    <Text style={{ whiteSpace: 'pre-line' }}>
                        {notification.description}
                    </Text>
                </MessageBar>
            )
        });

        if (elements.length > 0)
            return <div key={"notification.host"} style={{ borderTop: '1px solid gray' }}>
                {elements}
            </div>;
        else
            return <></>;
    }

    if (isPowerUser || isReadOnlyPowerUser) {
        menu.push(adminMenu());
    }

    return (
        <div style={styleStickyBar} ref={refStickyBar}>
            <CommandBar
                key={"commandBar"}
                styles={{
                    root: {
                        backgroundColor: theme.palette.white,
                        position: 'sticky',
                        top: '10px',
                    }
                }}
                items={menu}
                overflowItems={menuOverflow}
                overflowButtonProps={overflowProps}
                farItems={menuFar}
            />
            <div key={"breadcrumb"} style={{ paddingLeft: '44px', paddingRight: '44px' }}>
                <BreadcrumbNavigation />
            </div>

            {onRenderNotificationBar()}
        </div>
    )
};

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(EditorNavbar)